import { Redirect } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { css } from '@emotion/react';
import { useQuery } from '@apollo/client';
import { useUser } from '../providers/UserProvider';
import { font, colors, spacing } from '../config';
import { PUBLIC_LABELS } from '../queries/labels';
import { labelsReducer } from '../providers/LabelsProvider';
import { OverlayPortal } from './OverlayPortal';
import { Spinner } from './spinner/Spinner';
import { routes } from './RouteHandler';
import { labelTranslationsFallback } from '../utils/labelTranslationsFallback';
import { LoginLayout } from '../layouts/LoginLayout';
import { userHasNoCustomersKey } from '../constants/localStorageKeys';
import { generalApiErrorHandler } from '../utils/generalApiErrorHandler';
import { isLoggedInWithWrongIdentityProvider } from '../utils/authUtils';

const buttonStyle = css({
  width: '100%',
  color: colors.neutralColors.white,
  fontSize: font.size.medium,
  background: colors.boldColors.lightBlue.base,
  paddingTop: spacing.size4,
  paddingBottom: spacing.size4,
  marginBottom: spacing.size2,
  borderRadius: 4,
  border: 0,
  cursor: 'pointer',
  boxShadow: '0px 1px 0px rgba(1,4,16, .1), 0px -1px 0px rgba(0,0,0,.25) inset',
  '&:hover': {
    background: colors.boldColors.lightBlue.lightBlue80,
  },
});

const privacyCookieTextStyle = css({
  color: colors.neutralColors.slate90,
  fontSize: font.size.sizeM1,
  marginTop: spacing.size4,
  display: 'block',
});

const LoginPage = () => {
  const { isLoggedIn, selectedCustomer, cognitoData } = useUser();
  const userHasNoCustomers =
    window.localStorage.getItem(userHasNoCustomersKey) || false;

  const {
    data: publicLabelsData,
    loading: publicLabelsLoading,
    error: publicLabelsError,
  } = useQuery(PUBLIC_LABELS);

  if (publicLabelsLoading) {
    return (
      <OverlayPortal>
        <Spinner />
      </OverlayPortal>
    );
  }

  let labels = labelsReducer(publicLabelsData);

  if (publicLabelsError) {
    const { svy_snackbar_error_header } = labelTranslationsFallback;
    generalApiErrorHandler({
      error: publicLabelsError,
      snackbarHeader: svy_snackbar_error_header,
      snackbarMessage: 'Only English labels are currently available',
    });
    labels = labelTranslationsFallback;
  }

  const {
    getPublicLabels: { svy_loginpage_welcome_text, svy_sign_in_button },
    getPublicRichTextLabels: { svy_loginpage_privacy_cookie_policy_line },
  } = labels;

  if (isLoggedIn && isLoggedInWithWrongIdentityProvider(cognitoData)) {
    Auth.federatedSignIn({
      customProvider: process.env.FEDERATED_AUTH_PROVIDER,
    });
  }

  if (isLoggedIn && !selectedCustomer.id) {
    return <Redirect to={`/${routes.customerLogin}`} />;
  }

  if (isLoggedIn && !selectedCustomer.hasAcceptedTerms) {
    return <Redirect to={`/${routes.terms}`} />;
  }

  if (isLoggedIn && selectedCustomer.id) {
    return <Redirect to={`/${routes.homepage}`} />;
  }

  if (JSON.parse(userHasNoCustomers)) {
    window.localStorage.removeItem(userHasNoCustomersKey);
    return <Redirect to={`/${routes.noAssessments}`} />;
  }

  const LoginFooter = () => (
    <span
      css={privacyCookieTextStyle}
      dangerouslySetInnerHTML={{
        __html: svy_loginpage_privacy_cookie_policy_line,
      }}
    />
  );

  return (
    <LoginLayout
      showImage={true}
      title={svy_loginpage_welcome_text}
      footer={<LoginFooter />}
    >
      <button
        data-transaction-name="Sign in"
        aria-label="Sign in"
        css={buttonStyle}
        onClick={() =>
          Auth.federatedSignIn({
            customProvider: process.env.FEDERATED_AUTH_PROVIDER,
          })
        }
      >
        {svy_sign_in_button}
      </button>
    </LoginLayout>
  );
};

export { LoginPage };
